/**
 * MpesaPaymentModal
 *
 * Full M-Pesa STK Push flow:
 *   1. Phone number input
 *   2. STK Push trigger → backend
 *   3. Waiting / polling state while user enters PIN
 *   4. Success screen with receipt + SightTrails contacts
 *   5. Failure / timeout screen with retry
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    CheckCircleIcon,
    XCircleIcon,
    PhoneIcon,
    ArrowPathIcon,
    XMarkIcon,
    DocumentTextIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { currencyService } from '../../services/currencyService';
import { API_BASE_URL } from '../../config/apiConfig';

const POLL_INTERVAL_MS = 5000;
const TIMEOUT_SECONDS = 90;

// ─── Types ────────────────────────────────────────────────────────────────────

interface MpesaPaymentModalProps {
    isOpen: boolean;
    bookingId: string;
    tourName: string;
    amount: number;
    currency?: string;
    customerPhone?: string;
    onSuccess?: (receipt: string) => void;
    onClose: () => void;
    customMessage?: React.ReactNode;
}

type Phase = 'input' | 'waiting' | 'success' | 'failed';

// ─── Component ────────────────────────────────────────────────────────────────

export const MpesaPaymentModal: React.FC<MpesaPaymentModalProps> = ({
    isOpen,
    bookingId,
    tourName,
    amount,
    currency = 'KES',
    customerPhone = '',
    onSuccess,
    onClose,
    customMessage,
}) => {
    const [phase, setPhase] = useState<Phase>('input');
    const [phone, setPhone] = useState(customerPhone);
    const [transactionCode, setTransactionCode] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [checkoutId, setCheckoutId] = useState('');
    const [receipt, setReceipt] = useState('');
    const [failReason, setFailReason] = useState('');
    const [secondsLeft, setSecondsLeft] = useState(TIMEOUT_SECONDS);
    const [submitting, setSubmitting] = useState(false);
    const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
    const [isConverting, setIsConverting] = useState(false);

    const modalRef = useRef<HTMLDivElement>(null);
    const pollRef = useRef<NodeJS.Timeout | null>(null);
    const countdownRef = useRef<NodeJS.Timeout | null>(null);

    // ── Pre-conditional Logic ──────────────────────────────────────────────────

    // ── Immediate Conversion on Mount ──────────────────────────────────────────
    useEffect(() => {
        const convertToKsh = async () => {
            if (!isOpen) return; // Guard for heavy operations when closed
            if (currency === 'KES') {
                setConvertedAmount(amount);
                return;
            }
            setIsConverting(true);
            try {
                const ksh = await currencyService.convert(amount, currency || 'USD', 'KES');
                setConvertedAmount(ksh);
            } catch (err) {
                console.error('Failed to convert to KES:', err);
                setConvertedAmount(amount);
            } finally {
                setIsConverting(false);
            }
        };
        convertToKsh();
    }, [amount, currency, isOpen]);

    // ── Click Outside to Close ──────────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node) && phase !== 'waiting' && !submitting) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, phase, submitting, isOpen]);

    // ── Cleanup on unmount ──────────────────────────────────────────────────────
    useEffect(() => {
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, []);

    // ── Stop polling helpers ────────────────────────────────────────────────────
    const stopTimers = useCallback(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    }, []);

    // ── Polling for payment status ──────────────────────────────────────────────
    const startPolling = useCallback((cid: string) => {
        let elapsed = 0;
        stopTimers(); // Clear any existing

        countdownRef.current = setInterval(() => {
            elapsed += 1;
            setSecondsLeft(TIMEOUT_SECONDS - elapsed);
            if (elapsed >= TIMEOUT_SECONDS) {
                stopTimers();
                setPhase('failed');
                setFailReason('Payment timed out. Please try again.');
            }
        }, 1000);

        pollRef.current = setInterval(async () => {
            try {
                const { data } = await axios.get(`${API_BASE_URL}/payments/status/${cid}`);
                // Status can come back as 'paid' or 'completed'
                if (data.payment_status === 'completed' || data.status === 'paid') {
                    stopTimers();
                    setReceipt(data.transaction_id || cid);
                    setPhase('success');
                    if (onSuccess) onSuccess(data.transaction_id || cid);
                } else if (data.payment_status === 'failed') {
                    stopTimers();
                    setPhase('failed');
                    setFailReason('Payment was declined or cancelled. Please try again.');
                }
            } catch (err) {
                console.error('Polling error:', err);
                // Network hiccup — keep polling
            }
        }, POLL_INTERVAL_MS);
    }, [stopTimers, onSuccess]);

    // ── Initiate STK Push ────────────────────────────────────────────────────────
    const handleInitiateSTK = async () => {
        setSubmitting(true);
        setPhoneError('');
        
        try {
            const res = await axios.post(`${API_BASE_URL}/payments/mpesa/daraja/stkpush`, {
                phoneNumber: phone,
                amount: convertedAmount || amount,
                currency: convertedAmount ? 'KES' : (currency || 'USD'),
                bookingId: bookingId,
                accountReference: `ST-${bookingId.slice(0, 5).toUpperCase()}`,
                transactionDesc: `Safari: ${tourName}`
            });

            if (res.data.CheckoutRequestID) {
                setCheckoutId(res.data.CheckoutRequestID);
                setPhase('waiting');
                startPolling(res.data.CheckoutRequestID);
            } else {
                throw new Error('No CheckoutRequestID received');
            }
        } catch (err: any) {
            console.error('STK Push Error:', err);
            setPhoneError(err.response?.data?.error || 'Failed to trigger STK prompt. Please check your number or use manual payment.');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Simulate Success (Localhost Only) ───────────────────────────────────────
    const handleSimulateSuccess = async () => {
        if (!checkoutId) return;
        setSubmitting(true);
        try {
            await axios.post(`${API_BASE_URL}/payments/mpesa/daraja/callback`, {
                Body: {
                    stkCallback: {
                        MerchantRequestID: "MOCK-MERCHANT-ID",
                        CheckoutRequestID: checkoutId,
                        ResultCode: 0,
                        ResultDesc: "Success",
                        CallbackMetadata: {
                            Item: [
                                { Name: "Amount", Value: convertedAmount || amount },
                                { Name: "MpesaReceiptNumber", Value: "MOCK" + Math.random().toString(36).substring(7).toUpperCase() },
                                { Name: "PhoneNumber", Value: phone }
                            ]
                        }
                    }
                }
            });
            // Polling will pick it up
        } catch (err) {
            console.error('Simulation failed:', err);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Validate Transaction Code ──────────────────────────────────────────────────────────
    const validateTransactionCode = (value: string) => {
        const cleaned = value.trim();
        if (!cleaned) return 'Please enter the M-Pesa Transaction Code.';
        if (cleaned.length < 5) return 'Transaction code seems too short.';
        return '';
    };

    // ── Submit Manual Payment ─────────────────────────────────────────────────────────
    const handleManualVerify = async () => {
        const err = validateTransactionCode(transactionCode);
        if (err) { setPhoneError(err); return; }
        setPhoneError('');
        setSubmitting(true);

        try {
            const finalAmount = convertedAmount ? Math.round(convertedAmount) : Math.round(amount);
            const finalCurrency = convertedAmount ? 'KES' : (currency || 'USD');

            await axios.post(`${API_BASE_URL}/bookings/${bookingId}/payments`, {
                amount: finalAmount,
                currency: finalCurrency,
                paymentMethod: 'mpesa',
                paymentType: 'full_upfront',
                transactionReference: transactionCode.trim().toUpperCase(),
                status: 'pending'
            });

            setPhase('success');
            setTimeout(() => {
                if (onSuccess) {
                    onSuccess(transactionCode.trim().toUpperCase());
                }
            }, 3000);
        } catch (err: any) {
            setPhase('failed');
            setFailReason(err?.response?.data?.error || 'Failed to submit payment record. Please check your connection.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRetry = () => {
        stopTimers();
        setPhase('input');
        setFailReason('');
        setSecondsLeft(TIMEOUT_SECONDS);
        setCheckoutId('');
    };

    // UNCONDITIONAL RETURN GUARD
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[11000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in transition-all">
            <motion.div
                ref={modalRef}
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-md md:max-w-3xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-white/20 max-h-[90vh] flex flex-col"
            >
                {/* ── Close icon ──────────────────────────────────────────────────── */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50 p-3 bg-white hover:bg-gray-100 text-gray-800 rounded-full shadow-lg border border-gray-100 transition-all active:scale-95 group/close flex items-center justify-center"
                    aria-label="Close"
                >
                    <XMarkIcon className="w-6 h-6 group-hover/close:rotate-90 transition-transform duration-300" />
                </button>

                {/* ── Scrollable Content Area ────────────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto scrollbar-hide">
                    {/* ── Header ──────────────────────────────────────────────────────── */}
                    <div className="bg-white p-6 pb-4 relative overflow-hidden">
                        <div className="relative z-10 flex flex-col items-center text-center space-y-4">
                            <div className="w-32 h-16 flex items-center justify-center transition-transform duration-500 bg-white rounded-full shadow-[0_4px_20px_-4px_rgba(34,197,94,0.3)] border border-green-100 p-2">
                                <img src="/mpesa-logo.png" className="w-full h-full object-contain" alt="M-Pesa" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black tracking-tight text-slate-900">M-Pesa Checkout</h2>
                                <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mt-1">{tourName}</p>
                            </div>
                        </div>

                        <div className="mt-6 relative z-10 text-center">
                            {isConverting ? (
                                <div className="flex items-center justify-center space-x-3">
                                    <ArrowPathIcon className="w-6 h-6 animate-spin text-green-600" />
                                    <span className="text-xl font-black text-slate-400">Calculating KES…</span>
                                </div>
                            ) : (
                                <div className="bg-slate-50 rounded-[2rem] p-6 space-y-1 border-2 border-slate-100 shadow-inner">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Amount Payable</p>
                                    <p className="text-3xl font-black tracking-tighter text-green-600">KES {(convertedAmount || amount).toLocaleString()}</p>
                                    {currency !== 'KES' && (
                                        <p className="text-[10px] text-slate-400 font-bold">~ {currency} {amount.toLocaleString()}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Body ────────────────────────────────────────────────────────── */}
                    <div className="px-6 pb-12">
                        <AnimatePresence mode="wait">
                            {phase === 'input' && (
                                <motion.div
                                    key="input"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center bg-slate-50/50 p-2 rounded-[2.5rem]"
                                >
                                    <div className="rounded-[2rem] overflow-hidden border-2 border-white shadow-xl group transition-all hover:border-green-200 bg-white">
                                        <div className="bg-green-600 p-3 text-center">
                                            <p className="text-white font-black text-[10px] uppercase tracking-widest">Pay to Till Number</p>
                                        </div>
                                        <img
                                            src="/assets/payment/mpesa_till.png"
                                            alt="M-Pesa Till Number 3328989"
                                            className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700 max-h-64"
                                        />
                                        <div className="p-4 text-center bg-slate-50">
                                            <p className="text-[10px] font-black text-slate-400 uppercase">Till Number</p>
                                            <p className="text-2xl font-black text-slate-900">3328989</p>
                                        </div>
                                    </div>

                                    <div className="space-y-6 p-4">
                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">M-Pesa Phone Number</label>
                                                <input
                                                    type="text"
                                                    placeholder="0712 345 678"
                                                    value={phone}
                                                    onChange={e => { setPhone(e.target.value); setPhoneError(''); }}
                                                    className={`w-full p-4 bg-white rounded-2xl border-2 transition-all font-bold placeholder:text-slate-300 text-base shadow-inner ${phoneError && phase === 'input'
                                                        ? 'border-red-200 text-red-600 bg-red-50'
                                                        : 'border-slate-100 focus:border-green-500 text-slate-800'
                                                        }`}
                                                />
                                            </div>

                                            <button
                                                onClick={handleInitiateSTK}
                                                disabled={true}
                                                className="w-full py-4 rounded-2xl bg-green-600/50 text-white font-black shadow-xl transition-all disabled:opacity-60 flex flex-col items-center justify-center space-y-1 text-base cursor-not-allowed"
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <PhoneIcon className="w-5 h-5" />
                                                    <span className="line-through">Send STK Push</span>
                                                </div>
                                                <span className="text-[10px] uppercase font-bold text-green-100 tracking-widest bg-green-800/30 px-2 py-0.5 rounded">Under Maintenance</span>
                                            </button>
                                        </div>

                                        <div className="relative py-4">
                                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                                            <div className="relative flex justify-center text-[10px] uppercase font-black text-slate-400 bg-slate-50/50 px-4">Or Pay Manually</div>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Transaction Code</label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. QKT5G..."
                                                    value={transactionCode}
                                                    onChange={e => { setTransactionCode(e.target.value); setPhoneError(''); }}
                                                    className={`w-full p-4 bg-white rounded-2xl border-2 transition-all font-bold placeholder:text-slate-300 text-base shadow-inner ${phoneError && phase === 'input'
                                                        ? 'border-red-200 text-red-600 bg-red-50'
                                                        : 'border-slate-100 focus:border-green-500 text-slate-800'
                                                        }`}
                                                />
                                            </div>

                                            <button
                                                onClick={handleManualVerify}
                                                disabled={submitting || !transactionCode}
                                                className="w-full py-4 rounded-2xl bg-slate-900 border-2 border-slate-900 text-white font-black hover:bg-slate-800 transition-all active:scale-[0.98] text-sm shadow-xl disabled:opacity-50 flex items-center justify-center"
                                            >
                                                {submitting ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : 'Verify Transaction Code'}
                                            </button>
                                        </div>

                                        <p className="text-center text-[10px] text-slate-400 font-bold uppercase tracking-[0.3em]">
                                            Secure Checkout • SightTrails
                                        </p>
                                    </div>
                                </motion.div>
                            )}

                            {phase === 'waiting' && (
                                <motion.div
                                    key="waiting"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="text-center py-6 space-y-8"
                                >
                                    <div className="relative mx-auto w-32 h-32">
                                        <div className="absolute inset-0 rounded-full border-4 border-slate-50" />
                                        <div className="absolute inset-0 rounded-full border-4 border-t-green-600 animate-spin" />
                                        <PhoneIcon className="absolute inset-0 m-auto w-10 h-10 text-slate-900" />
                                    </div>
                                    <div className="space-y-2">
                                        <h3 className="text-2xl font-black text-slate-900">Authorize PIN</h3>
                                        <p className="text-slate-500 leading-relaxed max-w-xs mx-auto text-base font-medium">
                                            A prompt has been sent to your phone. Please enter your M-Pesa PIN.
                                        </p>
                                    </div>
                                    
                                    <div className="space-y-4">
                                        <div className="bg-slate-50 rounded-full py-3 px-6 inline-flex items-center space-x-3 text-slate-500 font-black text-[10px] uppercase tracking-widest border border-slate-100">
                                            <ArrowPathIcon className="w-4 h-4 animate-spin text-green-600" />
                                            <span>Syncing: {secondsLeft}s</span>
                                        </div>

                                        {/* Simulation Button for Testing */}
                                        <div className="pt-4">
                                            <button 
                                                onClick={handleSimulateSuccess}
                                                className="px-6 py-2 bg-yellow-100 text-yellow-800 text-[10px] font-black uppercase tracking-widest rounded-full border border-yellow-200 hover:bg-yellow-200 transition-all"
                                            >
                                                Simulate Success (Dev Mode)
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {phase === 'success' && (
                                <motion.div
                                    key="success"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="text-center py-6 space-y-6"
                                >
                                    <div className="w-32 h-32 bg-green-50 rounded-[3rem] flex items-center justify-center mx-auto shadow-inner text-green-600">
                                        <CheckCircleIcon className="w-20 h-20" />
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="text-3xl font-black text-slate-900">Paid Successfully!</h3>
                                        <p className="text-slate-500 font-bold text-base">Your adventure is officially booked.</p>
                                    </div>

                                    {receipt && (
                                        <div className="p-6 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200 text-left space-y-1">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Transaction Reference</p>
                                            <p className="font-mono text-xl font-black text-slate-800 tracking-tighter">{receipt}</p>
                                        </div>
                                    )}

                                    <button
                                        onClick={onClose}
                                        className="w-full py-4 rounded-[1.5rem] bg-primary-900 text-white font-black shadow-xl transition-all active:scale-[0.98] text-base"
                                    >
                                        View Booking Details
                                    </button>
                                </motion.div>
                            )}

                            {phase === 'failed' && (
                                <motion.div
                                    key="failed"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="text-center py-6 space-y-6"
                                >
                                    <div className="w-24 h-24 bg-red-50 rounded-[2rem] flex items-center justify-center mx-auto shadow-inner text-red-500">
                                        <XCircleIcon className="w-16 h-16" />
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="text-2xl font-black text-slate-900">Payment Failed</h3>
                                        <p className="text-slate-500 max-w-xs mx-auto font-bold text-sm">{failReason}</p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <button onClick={handleRetry} className="py-4 bg-slate-900 text-white font-black rounded-[1.5rem] transition-all text-base shadow-lg">Retry</button>
                                        <button onClick={onClose} className="py-4 bg-slate-100 text-slate-500 font-black rounded-[1.5rem] transition-all text-base border border-slate-200">Cancel</button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default MpesaPaymentModal;
